import { useCallback, useMemo, useRef, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler } from "../../../../common/hooks/use_debounced_callback";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { useContratoFilters } from "../hooks/contrato/filter";
import { zodResolver } from "@hookform/resolvers/zod";
import { downloadBase64File } from "../../../../common/utils/download_utils";
import { uploadFileToBase64StringValid } from "../../../../common/utils/utils";
import { MedicaoTerceirizacaoLista } from "../../domain/models/medicao_terceirizacao_lista";
import {
  MedicaoTerceirizacaoForm,
  MedicaoTerceirizacaoSchema,
} from "../schemas/Medicao/medicao_terceirizacao";
import { useMedicaoFilters } from "../hooks/medicao_terceirizacao/filter";
import { useMedicaoPagination } from "../hooks/medicao_terceirizacao/pagination";
import {
  ArquivoGenericoForm,
  ArquivoGenericoSchema,
} from "../schemas/Medicao/medicao_arquivo_genericos";
import { ArquivoGenericoLista } from "../../domain/models/arquivo_generico_lista";
import { ConfigPagamentoLista } from "../../domain/models/config_pagamento_lista";
import { ConfigPagamentoForm } from "../schemas/Medicao/config_pagamento";
import { ItemMedicaoTerceirizacaoLista } from "../../domain/models/config_item_mediacao_lista";
import { useItemMedicaoTerceirizacaoPagination } from "../hooks/item_medicao_terceirizacao/pagination";
import {
  ItemMedicaoTerceirizacaoForm,
  ItemMedicaoTerceirizacaoSchema,
} from "../schemas/Medicao/itens_medicao_terceirizacao";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";

type LoadMedicaoFuncState = {
  IdMedicaoTerceirizacao?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadPgtoMedicoesFuncState = {
  IdMedicaoTerceirizacao?: number;
  IdPgtoMedicao?: number;
  page?: number;
  rowsPerPage?: number;
};

type LoadArquivoGenericosFuncState = {
  IdArquivoGenerico?: number;
  IdTabela?: number | null;
  Ordem?: number | null;
  page?: number;
  rowsPerPage?: number;
};

type LoadItemMedicaoFuncState = {
  IdMedicaoTerceirizacao?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useMedicaoTerceirizacaoCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getFuncionarioByIdUsuarioUseCase,
    },
    terceirizacao: {
      getAllListagemMedicaoUseCase,
      createMedicaoUseCase,
      updateMedicaoUseCase,
      createArquivoGenericoUseCase,
      updateArquivoGenericoUseCase,
      getAllListagemArquivoGenericoUseCase,
      getAllListagemConfigPagamentoUseCase,
      createConfigPagamentoUseCase,
      deleteConfigPagamentoUseCase,
      getAllListagemItemMedicaoUseCase,
      createItemMedicaoUseCase,
      updateItemMedicaoUseCase,
      deleteItemMedicaoUseCase,
    },
  } = useDependencyInjection();
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const { idUsuario } = useUserAuthStore();
  const [funcionarioInfo, setFuncionarioInfo] =
    useState<FuncionarioInformation>();
  const { id } = useParams();
  const navigate = useNavigate();
  const [validFormulario, setValidFormulario] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selectedMedicao, setSelectedMedicao] =
    useState<MedicaoTerceirizacaoLista>();
  const [IdMedicao, setIdMedicao] = useState<number>(0);
  const [IdContrato, setIdContrato] = useState<number>(0);
  const { pushErrorNotification, pushWarnNotification, pushInfoNotification } =
    usePageLoader();
  const filters = useMedicaoFilters();
  const pagination = useMedicaoPagination();

  const hookForm = useForm<MedicaoTerceirizacaoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(MedicaoTerceirizacaoSchema),
    defaultValues: {
      Status: 0,
      FornecedorAvaliado: false,
      NotaFiscalEntregue: 0,
    },
  });
  //#region ArquivoGenericos
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] =
    useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [selectedArquivoGenericos, setSelectedArquivoGenericos] =
    useState<ArquivoGenericoLista>();
  const [ListaArquivoGenericos, setListaArquivoGenericos] = useState<
    ArquivoGenericoLista[]
  >([]);
  const [arquivo, setArquivo] = useState<string>();
  const [loadingArquivoGenericos, setLoadingArquivoGenericos] =
    useState<boolean>(false);
    const [medicaoStatus, setMedicaoStatus] = useState<number>(0);
  const hookFormArquivoGenericos = useForm<ArquivoGenericoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ArquivoGenericoSchema),
    defaultValues: {},
  });
  //#endregion

  //#region ConfigPagamento
  const [loadingConfigPagamento, setLoadingConfigPagamento] =
    useState<boolean>(false);
  const [savingConfigPagamento, setSavingConfigPagamento] =
    useState<boolean>(false);
  const [errorMessageConfigPagamento, setErrorMessageConfigPagamento] =
    useState<string>();
  const [configPagamentoLista, setConfigPagamentoLista] = useState<
    ConfigPagamentoLista[]
  >([]);
  const [configPagamentoModal, setConfigPagamentoModal] =
    useState<boolean>(false);
  const [selectedConfigPagamento, setSelectedConfigPagamento] = useState<
    ConfigPagamentoLista | undefined
  >(undefined);
  const [configPagamentoDeleteModal, setConfigPagamentoDeleteModal] =
    useState<boolean>(false);
  //#endregion

  //#region ItemMedicao
  const [openItemMedicao, setOpenItemMedicao] = useState<boolean>(false);
  const [itemMedicaoDeleteModal, setItemMedicaoDeleteModal] =
    useState<boolean>(false);
  const [ListaItemMedicao, setListaItemMedicao] = useState<
    ItemMedicaoTerceirizacaoLista[]
  >([]);
  const [selectedItemMedicao, setSelectedItemMedicao] =
    useState<ItemMedicaoTerceirizacaoLista>();
  const [loadingItemMedicao, setLoadingItemMedicao] = useState<boolean>(false);

  const paginationItemMedicao = useItemMedicaoTerceirizacaoPagination();

  const hookFormItemMedicao = useForm<ItemMedicaoTerceirizacaoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ItemMedicaoTerceirizacaoSchema),
    defaultValues: {
      IdSubconta: null,
    },
  });
  //#endregion

  const statusOptions = useMemo(() => {
    return [
      { id: 0, label: "Em Elaboração" },
      { id: 1, label: "Finalizada" },
      {
        id: 3,
        label: "Aprovada",
        disabled: selectedMedicao === null || selectedMedicao === undefined,
      },
      {
        id: 4,
        label: "Reprovada",
        disabled: selectedMedicao === null || selectedMedicao === undefined,
      },
    ];
  }, [selectedMedicao]);

  const { loadCentroCustoDropDown } = useDropDownLoader();

  // #region Funções de carregamento

  const loadFuncionarioInfo = useCallback(async () => {
    if (idUsuario) {
      try {
        const userInfo =
          await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(
            idUsuario,
          );
        setFuncionarioInfo(userInfo ?? undefined);
        return userInfo;
      } catch (e: unknown) {
        setFuncionarioInfo(undefined);
        throw new Error("Falha ao buscar dados do usuário!");
      }
    }
    return null;
  }, [idUsuario, getFuncionarioByIdUsuarioUseCase]);

  const loadMedicaoListaState = useCallback(
    async (funcState?: LoadMedicaoFuncState) => {
      try {
        const filterID = evaluateParamValue(
          funcState?.IdMedicaoTerceirizacao,
          filters.IdMedicaoTerceirizacao,
        );
        const page = funcState?.page ?? pagination.page;
        const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllListagemMedicaoUseCase.getAllListagemMedicao(
          {
            IdMedicaoTerceirizacao: filterID,
          },
          page,
          rows,
        );
        const medicao =
          getAllListagemMedicaoUseCase.items &&
          getAllListagemMedicaoUseCase.items.length > 0
            ? getAllListagemMedicaoUseCase.items[0]
            : null;
        if (medicao) {
          setSelectedMedicao(getAllListagemMedicaoUseCase.items[0]);
          if (Number(medicao.Status) !== 0) setValidFormulario(true);
          else setValidFormulario(false);

          setMedicaoStatus(Number(medicao.Status));

          hookForm.reset({
            DataAte: medicao.DataAte,
            DataDe: medicao.DataDe,
            DataMedicao: medicao.DataMedicao,
            Descricao: medicao.Descricao,
            Entregue: medicao.Entregue,
            FornecedorAvaliado: medicao.FornecedorAvaliado,
            IdContrato: medicao.IdContrato,
            IdFuncionarioResp: medicao.IdFuncionarioResp,
            IdMedicaoTerceirizacao: medicao.IdMedicaoTerceirizacao,
            IdSubconta: medicao.IdSubconta,
            InformacoesGerais: medicao.InformacoesGerais,
            NotaFiscalEntregue: medicao.NotaFiscalEntregue,
            NrMedicaoTerceirizacao: medicao.NrMedicaoTerceirizacao,
            NrNotaFiscal: medicao.NrNotaFiscal,
            NroNotaFiscal: medicao.NroNotaFiscal,
            PgtoProvisionado: medicao.PgtoProvisionado,
            Status: medicao.Status,
            Acrescimo:
              medicao.Acrescimo != null
                ? (medicao.Acrescimo.toFixed(2) as any)
                : null,
            Desconto:
              medicao.Desconto != null
                ? (medicao.Desconto.toFixed(2) as any)
                : null,
            Imposto:
              medicao.Imposto != null
                ? (medicao.Imposto.toFixed(2) as any)
                : null,

            PercRetencao:
              medicao.PercRetencao != null
                ? (medicao.PercRetencao.toFixed(2) as any)
                : null,
            ValorRetencao:
              medicao.ValorRetencao != null
                ? (medicao.ValorRetencao.toFixed(2) as any)
                : null,

            SubTotal:
              medicao.SubTotal != null
                ? (medicao.SubTotal.toFixed(2) as any)
                : null,
            ValorTotalCalculado:
              medicao.ValorTotalCalculado != null
                ? (medicao.ValorTotalCalculado.toFixed(2) as any)
                : null,
          });
        }
      } catch (e: unknown) {
        throw new Error("Falha ao buscar medicao!", );
      }
    },
    [
      filters.IdMedicaoTerceirizacao,
      getAllListagemMedicaoUseCase,
      hookForm,
      pagination.page,
      pagination.rowsPerPage,
    ],
  );

  const loadConfigPagListaState = useCallback(
    async (funcState?: LoadPgtoMedicoesFuncState) => {
      try {
        const filterID = funcState?.IdMedicaoTerceirizacao;
        const page = funcState?.page ?? pagination.page;
        const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemConfigPagamentoUseCase.getAllListagemConfigPagamento(
          {
            IdMedicaoTerceirizacao: filterID,
          },
          page,
          rows,
        );
        const pag =
          getAllListagemConfigPagamentoUseCase.items &&
          getAllListagemConfigPagamentoUseCase.items.length > 0
            ? getAllListagemConfigPagamentoUseCase.items[0]
            : null;

        if (pag && filterID && filterID > 0)
          setSelectedConfigPagamento(
            getAllListagemConfigPagamentoUseCase.items[0],
          );
        else setSelectedConfigPagamento(undefined);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar configuração de pagamento!");
      }
    },
    [
      getAllListagemConfigPagamentoUseCase,
      pagination.page,
      pagination.rowsPerPage,
    ],
  );

  const loadArquivoGenericosListaState = useCallback(
    async (funcState?: LoadArquivoGenericosFuncState) => {
      try {
        setLoadingArquivoGenericos(true);
        const filterID = funcState?.IdTabela;
        if (!filterID) {
          setLoadingArquivoGenericos(false);
          return;
        }
        await getAllListagemArquivoGenericoUseCase.getAllListagemArquivoGenerico(
          {
            IdTabela: filterID,
            IdArquivoGenerico: null,
            Ordem: null,
          },
          1,
          10,
        );

        const items = getAllListagemArquivoGenericoUseCase.items;

        const formData: any = {
          IdTabela: filterID,
          TipoTabela: 2,

          IdArquivoGenerico1: null,
          NomeArquivo1: null,
          Arquivo1: null,
          IdArquivoGenerico2: null,
          NomeArquivo2: null,
          Arquivo2: null,
          IdArquivoGenerico3: null,
          NomeArquivo3: null,
          Arquivo3: null,
        };

        if (Array.isArray(items) && items.length > 0) {
          items.forEach((item: any, index: number) => {
            formData.IdArquivoGenerico1 = item.IdArquivoGenerico1;
            formData.NomeArquivo1 = item.NomeArquivo1;
            formData.Arquivo1 = item.Arquivo1;

            formData.IdArquivoGenerico2 = item.IdArquivoGenerico2;
            formData.NomeArquivo2 = item.NomeArquivo2;
            formData.Arquivo2 = item.Arquivo2;

            formData.IdArquivoGenerico3 = item.IdArquivoGenerico3;
            formData.NomeArquivo3 = item.NomeArquivo3;
            formData.Arquivo3 = item.Arquivo3;
          });
        }
        setListaArquivoGenericos(items);
        hookFormArquivoGenericos.reset(formData);
        setSelectedArquivoGenericos(formData);
      } catch (e: unknown) {
        console.error(e);
      } finally {
        setLoadingArquivoGenericos(false);
      }
    },
    [getAllListagemArquivoGenericoUseCase, hookFormArquivoGenericos],
  );

  const loadItemMedicaoListaState = useCallback(
    async (funcState?: LoadItemMedicaoFuncState) => {
      try {
        setLoadingArquivoGenericos(true);
        const filterID = funcState?.IdMedicaoTerceirizacao;
        const page = funcState?.page ?? paginationItemMedicao.page;
        const rows =
          funcState?.rowsPerPage ?? paginationItemMedicao.rowsPerPage;

        await getAllListagemItemMedicaoUseCase.getAllListagemItemMedicao(
          {
            IdMedicaoTerceirizacao: filterID,
          },
          page,
          rows,
        );
        hookFormItemMedicao.reset();
        setListaItemMedicao(getAllListagemItemMedicaoUseCase.items);
        pagination.setRowsPerPage(getAllListagemItemMedicaoUseCase.pageSize);
        pagination.setTotalItems(getAllListagemItemMedicaoUseCase.totalItems);
      } catch (e: unknown) {
        console.error(e);
      } finally {
        setLoadingArquivoGenericos(false);
      }
    },
    [
      getAllListagemItemMedicaoUseCase,
      hookFormItemMedicao,
      pagination,
      paginationItemMedicao.page,
      paginationItemMedicao.rowsPerPage,
    ],
  );

  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      const info = await loadFuncionarioInfo();
      if (id) {
        setIdMedicao(Number(id));
        jobs.push([]);
        jobs.push(
          loadMedicaoListaState({ IdMedicaoTerceirizacao: Number(id) }),
        );
        jobs.push(
          loadItemMedicaoListaState({ IdMedicaoTerceirizacao: Number(id) }),
        );
        jobs.push(loadArquivoGenericosListaState({ IdTabela: Number(id) }));
      } else {
        if (info && info.idFuncionario) {
          hookForm.setValue("IdFuncionarioResp", info.idFuncionario);
        }
      }
      await loadCentroCustoDropDown((list) =>
        useContratoFilters.setState({ centroDeCustoLista: list }),
      );
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      const jobs = [];
      if (id) {
        setIdMedicao(Number(id));
        jobs.push([]);

        jobs.push(
          loadMedicaoListaState({ IdMedicaoTerceirizacao: Number(id) }),
        );
        jobs.push(
          loadItemMedicaoListaState({ IdMedicaoTerceirizacao: Number(id) }),
        );
        jobs.push(loadArquivoGenericosListaState({ IdTabela: Number(id) }));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    id,
    loadArquivoGenericosListaState,
    loadItemMedicaoListaState,
    loadMedicaoListaState,
    pushErrorNotification,
  ]);
  //#endregion

  // #region Eventos

  const handleArquivosSubmit = useCallback(
    async (form: ArquivoGenericoForm, idMedicaoOverride?: number) => {
      try {
        const idParaSalvar =
          idMedicaoOverride && idMedicaoOverride > 0
            ? idMedicaoOverride
            : IdMedicao;

        if (!idParaSalvar || idParaSalvar <= 0) {
          pushErrorNotification(
            "Erro",
            "Tentativa de salvar arquivo de Medição vinculado.",
          );
          return;
        }
        // 2. Prepara os Base64
        const arquivo1Base64 = form.Arquivo1
          ? await uploadFileToBase64StringValid(form.Arquivo1)
          : selectedArquivoGenericos?.Arquivo1 || null;
        const arquivo2Base64 = form.Arquivo2
          ? await uploadFileToBase64StringValid(form.Arquivo2)
          : selectedArquivoGenericos?.Arquivo2 || null;
        const arquivo3Base64 = form.Arquivo3
          ? await uploadFileToBase64StringValid(form.Arquivo3)
          : selectedArquivoGenericos?.Arquivo3 || null;
        if (form.IdTabela && form.IdTabela > 0) {
          await updateArquivoGenericoUseCase.updateArquivoGenerico(
            form.IdArquivoGenerico1 ?? null,
            form.IdArquivoGenerico2 ?? null,
            form.IdArquivoGenerico3 ?? null,
            idParaSalvar,
            form.NomeArquivo1 ?? null,
            arquivo1Base64,
            form.NomeArquivo2 ?? null,
            arquivo2Base64,
            form.NomeArquivo3 ?? null,
            arquivo3Base64,
            0,
            2,
          );
        } else {
          await createArquivoGenericoUseCase.createArquivoGenerico(
            idParaSalvar,
            form.NomeArquivo1 ?? null,
            arquivo1Base64,
            form.NomeArquivo2 ?? null,
            arquivo2Base64,
            form.NomeArquivo3 ?? null,
            arquivo3Base64,
            0,
            2,
          );
        }

        await loadArquivoGenericosListaState({ IdTabela: idParaSalvar });
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar Arquivos!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdMedicao,
      createArquivoGenericoUseCase,
      loadArquivoGenericosListaState,
      pushErrorNotification,
      selectedArquivoGenericos?.Arquivo1,
      selectedArquivoGenericos?.Arquivo2,
      selectedArquivoGenericos?.Arquivo3,
      updateArquivoGenericoUseCase,
    ],
  );
const isRevertingStatus = useRef(false);
  const handleMedicaoSubmit = useCallback(
    async (form: MedicaoTerceirizacaoForm) => {
      try {
        setSaving(true);
        let idMedicaoFinal = IdMedicao;
        if (IdMedicao) {
          await updateMedicaoUseCase.updateMedicao(
            IdMedicao,
            form.NrMedicaoTerceirizacao ?? null,
            form.IdContrato,
            form.DataMedicao ?? null,
            form.DataDe ?? null,
            form.DataAte ?? null,
            form.IdFuncionarioResp ?? null,
            form.Imposto ?? null,
            form.Acrescimo ?? null,
            form.Desconto ?? null,
            form.PercRetencao ?? null,
            form.ValorRetencao ?? null,
            form.InformacoesGerais ?? null,
            form.Status ?? null,
            form.PgtoProvisionado ?? null,
            form.FornecedorAvaliado ?? null,
            form.Entregue ?? null,
            form.NotaFiscalEntregue ?? null,
            form.NroNotaFiscal ?? null,
            form.NrNotaFiscal ?? null,
            form.IdSubconta ?? null,
            form.Descricao ?? null,
          );
          await loadMedicaoListaState({ IdMedicaoTerceirizacao: IdMedicao });
          pushInfoNotification("Sucesso", "Medição atualizado com sucesso!", {
            isAutoFinish: true,
          });
        } else {
          const idItem = await createMedicaoUseCase.createMedicao(
            form.NrMedicaoTerceirizacao ?? null,
            form.IdContrato,
            form.DataMedicao ?? null,
            form.DataDe ?? null,
            form.DataAte ?? null,
            form.IdFuncionarioResp ?? null,
            form.Imposto ?? null,
            form.Acrescimo ?? null,
            form.Desconto ?? null,
            form.PercRetencao ?? null,
            form.ValorRetencao ?? null,
            form.InformacoesGerais ?? null,
            form.Status ?? null,
            form.PgtoProvisionado ?? null,
            form.FornecedorAvaliado ?? null,
            form.Entregue ?? null,
            form.NotaFiscalEntregue ?? null,
            form.NroNotaFiscal ?? null,
            form.NrNotaFiscal ?? null,
            form.IdSubconta ?? null,
            form.Descricao ?? null,
          );
          setIdMedicao(Number(idItem));
          idMedicaoFinal = Number(idItem);
          await loadMedicaoListaState({ IdMedicaoTerceirizacao: idItem });
          pushInfoNotification("Sucesso", "Medição criado com sucesso!", {
            isAutoFinish: true,
          });
        }
        const formArquivos = hookFormArquivoGenericos.getValues();
        formArquivos.IdTabela = idMedicaoFinal;

        await handleArquivosSubmit(formArquivos, idMedicaoFinal);
        await loadMedicaoListaState({ IdMedicaoTerceirizacao: idMedicaoFinal });
      } catch (e) {
        isRevertingStatus.current = true;
        hookForm.setValue("Status", medicaoStatus);
          pushWarnNotification(
            "Alerta",
            processError(e, "Falha ao validar!")[0],
          );
      } finally {
        setSaving(false);
      }
    },
    [IdMedicao, createMedicaoUseCase, handleArquivosSubmit, hookForm, hookFormArquivoGenericos, loadMedicaoListaState, medicaoStatus, pushInfoNotification, pushWarnNotification, updateMedicaoUseCase],
  );

  //#region ItemMedicao
  const handleItemMedicao = useCallback(async () => {
    if (IdMedicao > 0 || selectedItemMedicao) {
      setItemMedicaoDeleteModal(false);
      setOpenItemMedicao(true);
      setSelectedItemMedicao(undefined);
      let id =
        IdMedicao > 0
          ? IdMedicao
          : selectedItemMedicao?.IdItemMedicaoTerceirizacao;
      hookFormItemMedicao.reset({
        Detalhamento: "",
        IdItemMedicaoTerceirizacao: 0,
        IdMedicaoTerceirizacao: id,
        IdProdutoServico: 0,
        IdSubconta: 0,
        Quantidade: 0,
        ValorTotal: 0,
        ValorUnitario: 0,
      });
    } else {
      setItemMedicaoDeleteModal(false);
      setOpenItemMedicao(false);
      return pushWarnNotification(
        "Atenção",
        "Para incluir um item de medição é necessário salvar a medição. !",
      );
    }
  }, [
    IdMedicao,
    hookFormItemMedicao,
    pushWarnNotification,
    selectedItemMedicao,
  ]);

  const handleItemMedicaoSubmit = useCallback(
    async (form: ItemMedicaoTerceirizacaoForm) => {
      try {
        if (IdMedicao && IdMedicao > 0) {
          if (form.IdItemMedicaoTerceirizacao) {
            await updateItemMedicaoUseCase.updateItemMedicao(
              form.IdItemMedicaoTerceirizacao,
              form.IdMedicaoTerceirizacao ?? null,
              form.IdProdutoServico ?? null,
              form.IdSubconta ?? null,
              form.Detalhamento ?? null,
              form.Quantidade ?? null,
              form.ValorUnitario ?? null,
              form.ValorTotal ?? null,
            );
            setOpenItemMedicao(false);
            pushInfoNotification(
              "Sucesso",
              "Item Medição atualizado com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createItemMedicaoUseCase.createItemMedicao(
              form.IdMedicaoTerceirizacao ?? null,
              form.IdProdutoServico ?? null,
              form.IdSubconta ?? null,
              form.Detalhamento ?? null,
              form.Quantidade ?? null,
              form.ValorUnitario ?? null,
              form.ValorTotal ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Item Medição criado com sucesso!",
              { isAutoFinish: true },
            );
            setOpenItemMedicao(false);
          }
          loadItemMedicaoListaState({ IdMedicaoTerceirizacao: IdMedicao });
          loadMedicaoListaState({ IdMedicaoTerceirizacao: IdMedicao });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Item Medição!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdMedicao,
      createItemMedicaoUseCase,
      loadItemMedicaoListaState,
      loadMedicaoListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateItemMedicaoUseCase,
    ],
  );

  const handleEdicaoItemMedicaoOpenState = useCallback(
    (IdItemMedicaoTerceirizacao: number | null, openState: boolean) => {
      if (openState) setOpenItemMedicao(true);

      if (IdItemMedicaoTerceirizacao) {
        const itemParaEditar = ListaItemMedicao.find(
          (x) => x.IdItemMedicaoTerceirizacao === IdItemMedicaoTerceirizacao,
        );
        setSelectedItemMedicao(itemParaEditar);
        if (itemParaEditar) {
          hookFormItemMedicao.reset({
            Detalhamento: itemParaEditar.Detalhamento,
            IdItemMedicaoTerceirizacao:
              itemParaEditar.IdItemMedicaoTerceirizacao,
            IdMedicaoTerceirizacao: itemParaEditar.IdMedicaoTerceirizacao,
            IdProdutoServico: itemParaEditar.IdProdutoServico ?? 0,
            Quantidade: itemParaEditar.Quantidade ?? 0,
            ValorTotal: itemParaEditar.ValorTotal,
            ValorUnitario: itemParaEditar.ValorUnitario ?? 0,
            IdSubconta: itemParaEditar.IdSubconta,
          });
        } else {
          hookFormItemMedicao.reset();
        }
      } else {
        hookFormItemMedicao.reset();
      }
    },
    [ListaItemMedicao, hookFormItemMedicao],
  );

  const handleDeleteItemMedicaoModalOpenState = useCallback(
    (IdItemMedicaoTerceirizacao: number | null, openState: boolean) => {
      if (openState) {
        if (IdMedicao && ListaItemMedicao && IdItemMedicaoTerceirizacao) {
          setSelectedItemMedicao(
            ListaItemMedicao.find(
              (x) =>
                x.IdItemMedicaoTerceirizacao === IdItemMedicaoTerceirizacao,
            ),
          );
        } else {
          setSelectedItemMedicao(undefined);
        }
      }
      setItemMedicaoDeleteModal(openState);
    },
    [IdMedicao, ListaItemMedicao],
  );

  const handleDeleteConfirmaItemMedicao = useCallback(async () => {
    if (selectedItemMedicao) {
      try {
        setSaving(true);
        await deleteItemMedicaoUseCase.deleteItemMedicao(
          selectedItemMedicao.IdItemMedicaoTerceirizacao,
        );
        await loadItemMedicaoListaState({ IdMedicaoTerceirizacao: IdMedicao });
        pushInfoNotification("Sucesso", "Item Medição deletado com sucesso!", {
          isAutoFinish: true,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
        );
      } finally {
        setSaving(false);
        setItemMedicaoDeleteModal(false);
      }
    }
  }, [
    selectedItemMedicao,
    deleteItemMedicaoUseCase,
    IdMedicao,
    loadItemMedicaoListaState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion

  //#region ARQUIVOS
  const handleDownload = useCallback(
    async (arquivoNome: string, arquivo: string) => {
      if (!saving) {
        try {
          await downloadBase64File(arquivoNome, arquivo);
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao fazer download do arquivo!"),
          );
        }
      }
    },
    [saving, pushErrorNotification],
  );

  const handleArquivoDeleteOpenState = useCallback(
    (
      campoNomeArquivo: string | null,
      campoArquivo: string | null,
      openState: boolean,
    ) => {
      if (openState && campoNomeArquivo && campoArquivo) {
        setOpenDeleteArquivoModal(true);
        setLimparArquivo(campoNomeArquivo);
        setArquivo(campoArquivo);
      } else {
        setOpenDeleteArquivoModal(false);
        setLimparArquivo(undefined);
        setArquivo(undefined);
      }
    },
    [],
  );

  const handleDeleteFile = useCallback(
    (campoNomeDoArquivo: string, campoDoArquivo: string) => {
      if (!saving) {
        hookFormArquivoGenericos.setValue(campoDoArquivo as any, null);
        hookFormArquivoGenericos.setValue(campoNomeDoArquivo as any, null);
        setSelectedArquivoGenericos((prev) => {
          if (!prev) return undefined;
          return {
            ...prev,
            [campoDoArquivo]: null,
            [campoNomeDoArquivo]: null,
          };
        });
      }
    },
    [hookFormArquivoGenericos, saving],
  );

  const handleConfirmarDeletarArquivo = useCallback(async () => {
    if (limparArquivo && arquivo) {
      handleDeleteFile(limparArquivo, arquivo);
      setOpenDeleteArquivoModal(false);
      setArquivo(undefined);
      setLimparArquivo(undefined);
    }
  }, [limparArquivo, arquivo, handleDeleteFile]);

  //#endregion

  //#region ConfigPagamento
  const handleCadastroConfigPagamentoOpenState = useCallback(
    (openState: boolean) => {
      if (openState) {
        loadConfigPagListaState({ IdMedicaoTerceirizacao: IdMedicao });
      }
      setConfigPagamentoModal(openState);
    },
    [IdMedicao, loadConfigPagListaState],
  );

  const handleConfigPagamentolSubmit = useCallback(
    async (form: ConfigPagamentoForm) => {
      setSaving(true);
      try {
        setSaving(true);

        await createConfigPagamentoUseCase.createConfigPagamento(
          IdMedicao,
          form.Nome ?? null,
          form.AVista ?? null,
          form.NrParcelas ?? null,
          form.TipoData ?? null,
          form.NroDias ?? null,
          form.ValorTotal ?? null,
          form.DataPgto ?? null,
          form.IdTipoConta ?? null,
          form.FormaPagamento ?? null,
          form.Observacao ?? null,
          form.InformacoesGerais ?? null,
          form.NrNotaFiscalEspecial ?? null,
          form.ValorEntrada ?? null,
          form.DataEntrada ?? null,
          form.IdSubconta ?? null,
          form.DiasEntreParcelas ?? null,
        );

        pushInfoNotification(
          "Sucesso",
          "Configuração de pagamento criado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Configuração de Pagamento!"),
        );
      } finally {
        setSaving(false);
        setConfigPagamentoModal(false);
      }
    },
    [
      IdMedicao,
      createConfigPagamentoUseCase,
      pushErrorNotification,
      pushInfoNotification,
    ],
  );

  const handleDeleteConfigPagamentoModalOpenState = useCallback(
    (openState: boolean) => {
      if (openState) {
        setConfigPagamentoDeleteModal(openState);
      }
    },
    [],
  );

  const handleDeleteConfirmaConfigPagamento = useCallback(async () => {
    if (selectedConfigPagamento) {
      try {
        setSaving(true);
        await deleteConfigPagamentoUseCase.deleteConfigPagamento(
          selectedConfigPagamento.IdPgtoMedicao,
        );
        await loadConfigPagListaState({ IdMedicaoTerceirizacao: IdMedicao });
        pushInfoNotification(
          "Sucesso",
          "Configuração Pagamento deletado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(
            e,
            "Não é possível excluir o pagamento. Existem parcelas que já foram baixadas ou alteradas pelo financeiro.!",
          ),
        );
      } finally {
        setSaving(false);
        setConfigPagamentoDeleteModal(false);
      }
    }
  }, [
    selectedConfigPagamento,
    deleteConfigPagamentoUseCase,
    loadConfigPagListaState,
    IdMedicao,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion
  const handleVoltarMedicao = useCallback(() => {
    navigate(`/MedicaoTerceirizacao_lista/`);
  }, [navigate]);

  //#endregion
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (IdMedicao) {
          changeHistory.handleOpenChangeHistory(
            IdMedicao,
            LogTipoTabela.MEDICAO_TERCEIRIZACAO,
            true,
          );
        }
      }
    },
    [IdMedicao, changeHistory],
  );
  return {
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    hookForm,
    hookFormArquivoGenericos,
    statusOptions,
    hookFormItemMedicao,
    IdContrato,
    selectedArquivoGenericos,
    openDeleteArquivoModal,
    selectedConfigPagamento,
    configPagamentoModal,
    configPagamentoLista,
    errorMessageConfigPagamento,
    savingConfigPagamento,
    loadingConfigPagamento,
    configPagamentoDeleteModal,
    selectedItemMedicao,
    openItemMedicao,
    IdMedicao,
    ListaItemMedicao,
    paginationItemMedicao,
    loadingItemMedicao,
    itemMedicaoDeleteModal,
    validFormulario,
    selectedMedicao,
    handleOpenChangeHistory,
    changeHistory,
    setValidFormulario,
    setOpenItemMedicao,
    setIdContrato,
    handleReload,
    handleVoltarMedicao,
    handleMedicaoSubmit,
    handleConfirmarDeletarArquivo,
    handleArquivoDeleteOpenState,
    handleDownload,
    handleArquivosSubmit,
    handleCadastroConfigPagamentoOpenState,
    handleConfigPagamentolSubmit,
    handleDeleteConfirmaConfigPagamento,
    handleDeleteConfigPagamentoModalOpenState,
    handleItemMedicao,
    handleItemMedicaoSubmit,
    handleEdicaoItemMedicaoOpenState,
    handleDeleteItemMedicaoModalOpenState,
    handleDeleteConfirmaItemMedicao,
    isRevertingStatus
  };
};
